import * as ko from 'knockout';

import * as dashboardApi from '../api/dashboard';
import * as dimensionMetasApi from '../api/dimension_metas';
import './include_columns_picker';
import {
  Choice,
  averageByChoices,
  averageByTooltip,
  formatChoices,
  layoutAxisChoices,
  headerLayoutChoices,
} from './overview_export_option_choices';

const template = require('raw-loader!../../templates/components/overview_export_button.html').default;

interface OverviewExportButtonParams {
  exportFormat: ko.Observable<dashboardApi.ExportFormat>;
  includeSiteId: ko.Observable<boolean>;
  includeSiteCoords: ko.Observable<boolean>;
  includeCustomerName: ko.Observable<boolean>;
  includeProtocol: ko.Observable<boolean>;
  includeOwner: ko.Observable<boolean>;
  includePhotoDescriptions: ko.Observable<boolean>;
  siteAttributeMetas: ko.ObservableArray<dimensionMetasApi.AttributeMetaData>;
  selectedSiteAttributeIds: ko.ObservableArray<number>;
  visitHandling: ko.Observable<dashboardApi.LayoutAxis>;
  dimensionHandling: ko.Observable<dashboardApi.LayoutAxis>;
  headerLayout: ko.Observable<dashboardApi.HeaderLayout>;
  averageBy: ko.Observable<dashboardApi.AverageBy>;
  isExporting: ko.Observable<boolean>;
  onDownload: () => void;
}

class OverviewExportButton {
  isOpen = ko.observable(false);
  panelTop = ko.observable(0);
  panelRight = ko.observable(0);
  panelMaxHeight = ko.observable(0);
  tooltipVisible = ko.observable(false);
  tooltipBottom = ko.observable(0);
  tooltipLeft = ko.observable(0);

  exportFormat: ko.Observable<dashboardApi.ExportFormat>;
  includeSiteId: ko.Observable<boolean>;
  includeSiteCoords: ko.Observable<boolean>;
  includeCustomerName: ko.Observable<boolean>;
  includeProtocol: ko.Observable<boolean>;
  includeOwner: ko.Observable<boolean>;
  includePhotoDescriptions: ko.Observable<boolean>;
  siteAttributeMetas: ko.ObservableArray<dimensionMetasApi.AttributeMetaData>;
  selectedSiteAttributeIds: ko.ObservableArray<number>;

  visitHandling: ko.Observable<dashboardApi.LayoutAxis>;
  dimensionHandling: ko.Observable<dashboardApi.LayoutAxis>;
  headerLayout: ko.Observable<dashboardApi.HeaderLayout>;
  averageBy: ko.Observable<dashboardApi.AverageBy>;
  isExporting: ko.Observable<boolean>;

  formatOptions: Choice<dashboardApi.ExportFormat>[] = formatChoices;
  visitHandlingOptions: Choice<dashboardApi.LayoutAxis>[] = layoutAxisChoices;
  dimensionHandlingOptions: Choice<dashboardApi.LayoutAxis>[] = layoutAxisChoices;
  headerLayoutOptions: Choice<dashboardApi.HeaderLayout>[] = headerLayoutChoices;
  averageByOptions: Choice<dashboardApi.AverageBy>[] = averageByChoices;
  averageByTooltip = averageByTooltip;

  private onDownload: () => void;
  // The fixed-positioned panel can't follow the trigger when the page scrolls
  // or resizes, so we close it rather than let it drift away from its anchor.
  private closeOnScroll = () => this.close();
  private tooltipAnchorRect: DOMRect | null = null;
  private tooltipRoot: HTMLElement | null = null;
  private tooltipFrameHandle: number | null = null;

  constructor(params: OverviewExportButtonParams) {
    this.exportFormat = params.exportFormat;
    this.includeSiteId = params.includeSiteId;
    this.includeSiteCoords = params.includeSiteCoords;
    this.includeCustomerName = params.includeCustomerName;
    this.includeProtocol = params.includeProtocol;
    this.includeOwner = params.includeOwner;
    this.includePhotoDescriptions = params.includePhotoDescriptions;
    this.siteAttributeMetas = params.siteAttributeMetas;
    this.selectedSiteAttributeIds = params.selectedSiteAttributeIds;
    this.visitHandling = params.visitHandling;
    this.dimensionHandling = params.dimensionHandling;
    this.headerLayout = params.headerLayout;
    this.averageBy = params.averageBy;
    this.isExporting = params.isExporting;
    this.onDownload = params.onDownload;
  }

  toggle = (_data: unknown, event: Event) => {
    if (this.isOpen()) {
      this.close();
      return;
    }
    const triggerElement = event.currentTarget as HTMLElement | null;
    if (!triggerElement) return;
    const rect = triggerElement.getBoundingClientRect();
    const viewportMargin = 16;
    this.panelTop(rect.bottom);
    this.panelRight(window.innerWidth - rect.right);
    this.panelMaxHeight(Math.max(0, window.innerHeight - rect.bottom - viewportMargin));
    window.addEventListener('scroll', this.closeOnScroll, { passive: true });
    window.addEventListener('resize', this.closeOnScroll);
    this.isOpen(true);
  };

  close = () => {
    if (!this.isOpen()) return;
    window.removeEventListener('scroll', this.closeOnScroll);
    window.removeEventListener('resize', this.closeOnScroll);
    this.isOpen(false);
  };

  download = () => {
    if (this.isExporting()) return;
    this.close();
    this.onDownload();
  };

  /**
   * Anchors the tooltip near the icon and defers placement to a rAF so the
   * rendered width can be measured and clamped within the viewport.
   *
   * @param _data Unused; required by the KO event binding signature.
   * @param event KO-bound DOM event; `currentTarget` is the icon used as the anchor.
   */
  showTooltip = (_data: unknown, event: Event) => {
    const iconElement = event.currentTarget as HTMLElement | null;
    if (!iconElement) return;
    this.tooltipAnchorRect = iconElement.getBoundingClientRect();
    this.tooltipRoot = iconElement.closest<HTMLElement>('.overview-export-button');
    const gapAboveIcon = 8;
    this.tooltipBottom(window.innerHeight - this.tooltipAnchorRect.top + gapAboveIcon);
    this.tooltipLeft(-9999);
    this.tooltipVisible(true);
    this.tooltipFrameHandle = window.requestAnimationFrame(this.placeTooltip);
  };

  private placeTooltip = () => {
    this.tooltipFrameHandle = null;
    if (!this.tooltipVisible() || !this.tooltipAnchorRect || !this.tooltipRoot) return;
    const tooltipElement = this.tooltipRoot.querySelector<HTMLElement>('.portaled-tooltip');
    if (!tooltipElement) return;
    const halfWidth = tooltipElement.offsetWidth / 2;
    const anchorCenterX = this.tooltipAnchorRect.left + this.tooltipAnchorRect.width / 2;
    const clampedLeft = Math.min(
      window.innerWidth - halfWidth,
      Math.max(halfWidth, anchorCenterX)
    );
    this.tooltipLeft(clampedLeft);
  };

  hideTooltip = () => {
    if (this.tooltipFrameHandle !== null) {
      window.cancelAnimationFrame(this.tooltipFrameHandle);
      this.tooltipFrameHandle = null;
    }
    this.tooltipAnchorRect = null;
    this.tooltipRoot = null;
    this.tooltipVisible(false);
  };

  dispose() {
    this.close();
    this.hideTooltip();
  }
}

ko.components.register('overview-export-button', {
  viewModel: OverviewExportButton,
  template,
});

export {};
